import type { PayoutRail, SinglePayee, SinglePayment } from '@/types/portal';
import { imageUrl } from '@/utils/assets';
import { PLATFORM } from '@/config/platform';

/**
 * Payout rails available per market, grouped by the destination channel a
 * single payment can take: wallet-to-wallet, mobile money or bank transfer.
 * Rails never cross a currency — settlement always happens on the local rail.
 */
export const payoutRails: PayoutRail[] = [
  // ---- KES ----
  {
    id: 'rail-wallet-kes',
    name: PLATFORM.walletName,
    currency: 'KES',
    channel: 'wallet',
    icon: 'ri-wallet-3-line',
    description: `Instant wallet-to-wallet transfer to a ${PLATFORM.payName} Wallet`,
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-mpesa-kes',
    name: 'M-Pesa',
    currency: 'KES',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'Safaricom mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-airtel-kes',
    name: 'Airtel Money',
    currency: 'KES',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'Airtel mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-bank-kes',
    name: 'Bank transfer',
    currency: 'KES',
    channel: 'bank',
    icon: 'ri-bank-line',
    description: 'Direct settlement to a Kenyan bank account',
    eta: '1–2 business days',
    fee: 150,
    status: 'Wallet active',
  },
  // ---- TZS ----
  {
    id: 'rail-wallet-tzs',
    name: PLATFORM.walletName,
    currency: 'TZS',
    channel: 'wallet',
    icon: 'ri-wallet-3-line',
    description: `Instant wallet-to-wallet transfer to a ${PLATFORM.payName} Wallet`,
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-mpesa-tzs',
    name: 'M-Pesa',
    currency: 'TZS',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'Vodacom mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-tigo-tzs',
    name: 'Tigo Pesa',
    currency: 'TZS',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'Tigo mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-bank-tzs',
    name: 'Bank transfer',
    currency: 'TZS',
    channel: 'bank',
    icon: 'ri-bank-line',
    description: 'Direct settlement to a Tanzanian bank account',
    eta: '1–2 business days',
    fee: 3000,
    status: 'Wallet active',
  },
  // ---- UGX ----
  {
    id: 'rail-wallet-ugx',
    name: PLATFORM.walletName,
    currency: 'UGX',
    channel: 'wallet',
    icon: 'ri-wallet-3-line',
    description: `Instant wallet-to-wallet transfer to a ${PLATFORM.payName} Wallet`,
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-mtn-ugx',
    name: 'MTN MoMo',
    currency: 'UGX',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'MTN mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-airtel-ugx',
    name: 'Airtel Money',
    currency: 'UGX',
    channel: 'mobile',
    icon: 'ri-smartphone-line',
    description: 'Airtel mobile money wallet',
    eta: 'Instant',
    fee: 0,
    status: 'Wallet active',
  },
  {
    id: 'rail-bank-ugx',
    name: 'Bank transfer',
    currency: 'UGX',
    channel: 'bank',
    icon: 'ri-bank-line',
    description: 'Direct settlement to a Ugandan bank account',
    eta: '1–2 business days',
    fee: 5000,
    status: 'Wallet active',
  },
];

/**
 * Every payee a single payment can reach: creators engaged on campaigns
 * (with their approved invoice for reconciliation) and one-off suppliers.
 */
export const singlePayees: SinglePayee[] = [
  {
    id: 'pay-furaha',
    kind: 'creator',
    creatorId: 'crt-furaha',
    name: 'Furaha Salim',
    handle: '@furaha.salim',
    avatar: imageUrl('creators/crt-furaha.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2314',
      mobileNumber: '254712345678',
      network: 'M-Pesa',
      accountNumber: '0110293847561',
      bankName: 'KCB Bank',
    },
    invoices: [
      {
        number: 'INV-2416',
        campaignId: 'cmp-glowrite-q4',
        campaign: 'GlowRite Skincare Q4 Push',
        amount: 32000,
        advance: { percent: 80, amount: 25600, fee: 720, advancedAt: '2026-09-08', reference: 'ADV-3301' },
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-grace',
    kind: 'creator',
    creatorId: 'crt-grace',
    name: 'Grace Wambui',
    handle: '@grace.wambui',
    avatar: imageUrl('creators/crt-grace.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2087',
      mobileNumber: '254722908877',
      network: 'M-Pesa',
      accountNumber: '1102938475620',
      bankName: 'Equity Bank',
    },
    invoices: [
      {
        number: 'INV-2432',
        campaignId: 'cmp-glowrite-q4',
        campaign: 'GlowRite Skincare Q4 Push',
        amount: 76000,
        advance: { percent: 70, amount: 53200, fee: 1520, advancedAt: '2026-09-10', reference: 'ADV-3302' },
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-sanaa',
    kind: 'creator',
    creatorId: 'crt-sanaa',
    name: 'Sanaa Njeri',
    handle: '@sanaa.cooks',
    avatar: imageUrl('creators/crt-sanaa.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2451',
      mobileNumber: '254733114422',
      network: 'M-Pesa',
      accountNumber: '0120345678991',
      bankName: 'Co-operative Bank',
    },
    invoices: [
      {
        number: 'INV-2433',
        campaignId: 'cmp-farmfresh',
        campaign: 'FarmFresh Groceries Weekly',
        amount: 42000,
        readiness: 'ready',
        issues: [],
      },
      {
        number: 'INV-2418',
        campaignId: 'cmp-glowrite-q4',
        campaign: 'GlowRite Skincare Q4 Push',
        amount: 42000,
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-zawadi',
    kind: 'creator',
    creatorId: 'crt-zawadi',
    name: 'Zawadi Mwangi',
    handle: '@zawadi.glow',
    avatar: imageUrl('creators/crt-zawadi.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2199',
      mobileNumber: '254701556677',
      network: 'Airtel Money',
      accountNumber: '0160778899004',
      bankName: 'Absa Bank',
    },
    invoices: [
      {
        number: 'INV-2434',
        campaignId: 'cmp-glowrite-q4',
        campaign: 'GlowRite Skincare Q4 Push',
        amount: 64000,
        advance: { percent: 50, amount: 32000, fee: 960, advancedAt: '2026-09-11', reference: 'ADV-3304' },
        readiness: 'review',
        issues: ['Supporting reel still in content review'],
      },
    ],
  },
  {
    id: 'pay-tunde',
    kind: 'creator',
    creatorId: 'crt-tunde',
    name: 'Tunde Bakari',
    handle: '@tunde.beats',
    avatar: imageUrl('creators/crt-tunde.webp'),
    market: 'Tanzania',
    currency: 'TZS',
    status: 'Wallet active',
    target: {
      walletId: '3341',
      mobileNumber: '255712998877',
      network: 'Tigo Pesa',
      accountNumber: '0230456781234',
      bankName: 'CRDB Bank',
    },
    invoices: [
      {
        number: 'INV-2409',
        campaignId: 'cmp-tusker-weekend',
        campaign: 'Tusker Lite Weekend Vibes',
        amount: 6800000,
        advance: { percent: 60, amount: 4080000, fee: 122400, advancedAt: '2026-09-04', reference: 'ADV-3303' },
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-halima',
    kind: 'creator',
    creatorId: 'crt-halima',
    name: 'Halima Yusuf',
    handle: '@halima.heritage',
    avatar: imageUrl('creators/crt-halima.webp'),
    market: 'Tanzania',
    currency: 'TZS',
    status: 'Wallet active',
    target: {
      walletId: '3120',
      mobileNumber: '255754123456',
      network: 'M-Pesa',
      accountNumber: '0150998877665',
      bankName: 'NMB Bank',
    },
    invoices: [
      {
        number: 'INV-2394',
        campaignId: 'cmp-ubuntu-cosmetics',
        campaign: 'Ubuntu Cosmetics Heritage',
        amount: 4200000,
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-emmanuel',
    kind: 'creator',
    creatorId: 'crt-emmanuel',
    name: 'Emmanuel Okello',
    handle: '@emmanuel.sports',
    avatar: imageUrl('creators/crt-emmanuel.webp'),
    market: 'Uganda',
    currency: 'UGX',
    status: 'Wallet active',
    target: {
      walletId: '4412',
      mobileNumber: '256772445566',
      network: 'MTN MoMo',
      accountNumber: '0310456789012',
      bankName: 'Stanbic Bank',
    },
    invoices: [
      {
        number: 'INV-2436',
        campaignId: 'cmp-tusker-weekend',
        campaign: 'Tusker Lite Weekend Vibes',
        amount: 4200000,
        readiness: 'ready',
        issues: [],
      },
    ],
  },
  {
    id: 'pay-david',
    kind: 'creator',
    creatorId: 'crt-david',
    name: 'David Mwansa',
    handle: '@david.travels',
    avatar: imageUrl('creators/crt-david.webp'),
    market: 'Uganda',
    currency: 'UGX',
    status: 'Wallet active',
    target: {
      walletId: '4288',
      mobileNumber: '256701223344',
      network: 'Airtel Money',
      accountNumber: '0140776655443',
      bankName: 'Centenary Bank',
    },
    invoices: [
      {
        number: 'INV-2437',
        campaignId: 'cmp-safipay-launch',
        campaign: 'SafiPay Fintech Launch',
        amount: 2100000,
        readiness: 'review',
        issues: ['SafiPay budget line needs sign-off'],
      },
    ],
  },
  {
    id: 'pay-jabari',
    kind: 'creator',
    creatorId: 'crt-jabari',
    name: 'Jabari Kimani',
    handle: '@jabari.wellness',
    avatar: imageUrl('creators/crt-jabari.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet inactive',
    target: {
      walletId: '2077',
      mobileNumber: '254745889900',
      network: 'M-Pesa',
      accountNumber: '0170887766554',
      bankName: 'NCBA Bank',
    },
    invoices: [
      {
        number: 'INV-2439',
        campaignId: 'cmp-jua-health',
        campaign: 'Jua Health Awareness',
        amount: 88000,
        readiness: 'blocked',
        issues: ['Wallet inactive — last payout failed'],
      },
    ],
  },
  {
    id: 'pay-neema',
    kind: 'creator',
    creatorId: 'crt-neema',
    name: 'Neema Achieng',
    handle: '@neema.money',
    avatar: imageUrl('creators/crt-neema.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2333',
      mobileNumber: '254799001122',
      network: 'M-Pesa',
      accountNumber: '0190334455667',
      bankName: 'Family Bank',
    },
    invoices: [
      {
        number: 'INV-2440',
        campaignId: 'cmp-safipay-launch',
        campaign: 'SafiPay Fintech Launch',
        amount: 130000,
        readiness: 'blocked',
        issues: ['KYC incomplete — ID document pending'],
      },
    ],
  },
  {
    id: 'pay-brian',
    kind: 'creator',
    creatorId: 'crt-brian',
    name: 'Brian Otieno',
    handle: '@brian.weekend',
    avatar: imageUrl('creators/crt-brian.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Wallet active',
    target: {
      walletId: '2260',
      mobileNumber: '254790112233',
      network: 'M-Pesa',
      accountNumber: '0110998877665',
      bankName: 'Equity Bank',
    },
    invoices: [
      {
        number: 'INV-2411',
        campaignId: 'cmp-tusker-weekend',
        campaign: 'Tusker Lite Weekend Vibes',
        amount: 74500,
        readiness: 'review',
        issues: ['Final reel not yet submitted'],
      },
    ],
  },
  // ---- Suppliers: one-off payees with no campaign tie ----
  {
    id: 'pay-supplier-studio',
    kind: 'supplier',
    name: 'Kampala Studio Rentals',
    avatar: imageUrl('payments/pay-supplier-studio.webp'),
    market: 'Uganda',
    currency: 'UGX',
    status: 'Verified',
    target: {
      mobileNumber: '256778889900',
      network: 'MTN MoMo',
      accountNumber: '0320788990012',
      bankName: 'Stanbic Bank',
    },
    invoices: [],
  },
  {
    id: 'pay-supplier-lens',
    kind: 'supplier',
    name: 'Lens & Light Photography',
    avatar: imageUrl('payments/pay-supplier-lens.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Verified',
    target: {
      walletId: '5901',
      mobileNumber: '254711223344',
      network: 'M-Pesa',
    },
    invoices: [],
  },
  {
    id: 'pay-supplier-props',
    kind: 'supplier',
    name: 'Nairobi Props Co',
    avatar: imageUrl('payments/pay-supplier-props.webp'),
    market: 'Kenya',
    currency: 'KES',
    status: 'Verified',
    target: {
      walletId: '5760',
      accountNumber: '0130567891234',
      bankName: 'KCB Bank',
    },
    invoices: [],
  },
  {
    id: 'pay-supplier-sound',
    kind: 'supplier',
    name: 'Bongo Sound Works',
    avatar: imageUrl('payments/pay-supplier-sound.webp'),
    market: 'Tanzania',
    currency: 'TZS',
    status: 'Verified',
    target: {
      mobileNumber: '255786654321',
      network: 'Tigo Pesa',
      accountNumber: '0260112233445',
      bankName: 'NMB Bank',
    },
    invoices: [],
  },
];

export const recentSinglePayments: SinglePayment[] = [
  {
    id: 'sp-9',
    reference: 'PAY-77129',
    payeeName: 'Grace Wambui',
    avatar: imageUrl('creators/crt-grace.webp'),
    channel: 'bank',
    target: 'Equity Bank · 1102938475',
    campaign: 'GlowRite Skincare Q4 Push',
    amount: 22800,
    currency: 'KES',
    rail: 'Bank transfer · KES',
    status: 'Paid',
    createdAt: '12 Sep 2026',
    createdBy: 'Peter Kironji',
  },
  {
    id: 'sp-8',
    reference: 'PAY-77128',
    payeeName: 'Tunde Bakari',
    avatar: imageUrl('creators/crt-tunde.webp'),
    channel: 'mobile',
    target: 'Tigo Pesa · 255712998877',
    campaign: 'Tusker Lite Weekend Vibes',
    amount: 2720000,
    currency: 'TZS',
    rail: 'Tigo Pesa · TZS',
    status: 'Processing',
    createdAt: '11 Sep 2026',
    createdBy: 'Grace Kim',
  },
  {
    id: 'sp-7',
    reference: 'PAY-77124',
    payeeName: 'Emmanuel Okello',
    avatar: imageUrl('creators/crt-emmanuel.webp'),
    channel: 'mobile',
    target: 'MTN MoMo · 256772445566',
    campaign: 'Tusker Lite Weekend Vibes',
    amount: 4200000,
    currency: 'UGX',
    rail: 'MTN MoMo · UGX',
    status: 'Paid',
    createdAt: '9 Sep 2026',
    createdBy: 'Peter Kironji',
  },
  {
    id: 'sp-6',
    reference: 'PAY-77120',
    payeeName: 'Lens & Light Photography',
    avatar: imageUrl('payments/pay-supplier-lens.webp'),
    channel: 'wallet',
    target: 'Wallet 5901',
    amount: 45000,
    currency: 'KES',
    rail: `${PLATFORM.walletName} · KES`,
    status: 'Paid',
    createdAt: '7 Sep 2026',
    createdBy: 'Grace Kim',
  },
];

/** Next sequential single-payment reference number. */
export const nextPaymentNumber = 77130;